// ============================================================
// A BODY'S NATURES (plan body-map T2; the bench's trait effects, ruled
// 2026-10-09) - what it is, not what happened to it:
//
//   Incorporeal          hollow: a faint body in a bright pulsing outline;
//                        an ordinary hit passes through it (a jolt, no wound)
//   Phase                its other positions: shells breathing out and back
//   Hoverer, Flyer       the figure stays put; its lift is told by what is
//                        under it - a hoverer's small shadow, rippling rings
//                        and haze; a flyer's far shadow and downdraft
//   Stuff of Nightmares  ink and shadow round it, and a shadow of it whose
//                        head and arms move on their own, things it never did
//   Warp Instability     on a hit its hold on the world slips: shells fling
//                        out, hang, and rewind into it (the backtrack)
//   Undying              the last of its wounds will not go out (a pulse)
//
// Pure. Zero React dependency. Frame units; seconds.
// ============================================================

import type { Box } from './frame';
import { seeded } from './hash';
import { loop } from './tracks';

/** How long one turn of a wrong shadow's part lasts, from one rest or angle to the next. */
const KNOT = 1.6;

/** A part's angle at a knot: at rest 40% of the time, else any way by up to its reach (seeded by the part). */
function knotAngle(part: string, k: number, max: number): number {
  const r = seeded(`wrong:${part}`);
  if (r(`rest${k}`) < 0.4) return 0;
  return (r(`dir${k}`) < 0.5 ? -1 : 1) * max * (0.4 + 0.6 * r(`by${k}`));
}

/**
 * The angle (degrees) one part of the wrong shadow is turned at `t`: eased from knot to knot, each knot its own
 * seeded rest or turn - a part moving on its own, never in a jump, the same on every screen.
 */
export function wrongAngle(part: string, max: number, t: number): number {
  const k = Math.floor(t / KNOT);
  const f = t / KNOT - k;
  const ease = f * f * (3 - 2 * f);
  return knotAngle(part, k, max) + (knotAngle(part, k + 1, max) - knotAngle(part, k, max)) * ease;
}

/** The wrong shadow's parts: the head turns about the base of the neck, the arms about the shoulders. */
export const WRONG_PARTS = [
  { part: 'head', max: 28 },
  { part: 'rightArm', max: 45 },
  { part: 'leftArm', max: 45 },
] as const;

/** Where a wrong-shadow part turns about, in its box: a shoulder (outer top) or the neck's base. */
export function wrongPivot(part: string, [x0, y0, x1, y1]: Box): readonly [number, number] {
  if (part === 'head') return [(x0 + x1) / 2, y1];
  return [part === 'rightArm' ? x1 - (x1 - x0) * 0.15 : x0 + (x1 - x0) * 0.15, y0 + 120];
}

/** The ink and shadow round a nightmare: four dense black copies drifting behind it, each on its own slow loop. */
export const NIGHT_SHADOWS = [
  [-70, 40],
  [80, 24],
  [10, 84],
  [-30, -40],
].map(([dx, dy], k) => ({ dx, dy, dur: 7 + 2 * k }));

/** A hoverer's or a flyer's lift, under a figure with these bounds. */
export function liftLook([x0, y0, x1, y1]: Box, kind: 'hover' | 'fly') {
  const cx = (x0 + x1) / 2;
  const w = (x1 - x0) * 0.5;
  return kind === 'hover' ? hoverLook(cx, w, y1) : flyLook(cx, w, y0, y1);
}

/** A hoverer: a small shadow just below it, rings rippling out from under it, and a haze. */
function hoverLook(cx: number, w: number, y1: number) {
  const gy = y1 + 90;
  return {
    shadow: { cx, cy: gy, rx: w * 0.6, ry: 46, opacity: 0.8, dashed: false },
    rings: [0, 1, 2].map((k) => ({
      rx: loop('rx', [w * 0.3, w * 1.2], 2.4, { offset: k * 0.8 }),
      ry: loop('ry', [18, 70], 2.4, { offset: k * 0.8 }),
      opacity: loop('opacity', [0.95, 0], 2.4, { offset: k * 0.8 }),
    })),
    haze: {
      x: cx - w * 0.4,
      y: y1 - 30,
      width: w * 0.8,
      height: 130,
      opacity: loop('opacity', [0.12, 0.3, 0.12], 1.3),
    },
    streaks: [],
    cx,
    gy,
  };
}

/** A flyer: a far dashed shadow, and a downdraft from a share of its own height above its feet to the ground. */
function flyLook(cx: number, w: number, y0: number, y1: number) {
  const gy = y1 + 230;
  const top = y1 - (y1 - y0) * 0.3;
  return {
    shadow: { cx, cy: gy, rx: w * 0.9, ry: 55, opacity: 0.6, dashed: true },
    rings: [],
    haze: null,
    streaks: Array.from({ length: 9 }, (_, k) => {
      const [dur, offset] = [0.9 + 0.13 * (k % 3), (k * 0.37) % 1];
      return {
        x: cx + (k - 4) * w * 0.26,
        y1: loop('y1', [top, gy - 120], dur, { offset }),
        y2: loop('y2', [top + 160, gy], dur, { offset }),
        opacity: loop('opacity', [0, 0.6, 0], dur, { offset }),
      };
    }),
    cx,
    gy,
  };
}

/** Phase's shells: where each drifts to, how bright, its beat and its phase (the bench's four). */
export const PHASE_SHELLS = [
  { dx: -150, dy: -20, opacity: 0.42, offset: 0, dur: 5.2 },
  { dx: 150, dy: 10, opacity: 0.42, offset: 1.3, dur: 6.1 },
  { dx: -70, dy: -60, opacity: 0.26, offset: 2.6, dur: 7.3 },
  { dx: 80, dy: 50, opacity: 0.26, offset: 3.4, dur: 6.7 },
];

/** Warp Instability's backtrack, a beat after a hit: four shells flung out, hanging, then rewound fast into it. */
export const BACKTRACK = {
  delay: 0.2,
  dur: 0.9,
  shells: [
    [-430, -40],
    [430, 25],
    [-210, -150],
    [240, 120],
  ].map(([dx, dy], k) => ({ dx, dy, opacity: Number((0.55 - 0.08 * k).toFixed(2)) })),
};
